import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import PageLayout from "./PageLayout";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card } from "./ui/card";
import usePageMeta from "../hooks/usePageMeta";
import Iridescence from "./Iridescence";
import { IRIDESCENCE_DARK, IRIDESCENCE_LIGHT } from "../lib/iridescence";

const MemoIridescence = React.memo(Iridescence);

const SpeedTestPage = () => {
  const { theme } = useTheme();
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  usePageMeta({
    title: "Test de velocidad | RG Servicio Técnico",
    description:
      "Test de velocidad de internet con LibreSpeed. Medición simple y rápida.",
    canonical: "https://rgserviciotecnico.com/velocidad",
  });

  return (
    <PageLayout
      className="relative bg-gradient-to-br from-[#f9fbff] via-white to-[#eef7ff] dark:from-[#07070b] dark:via-[#120b14] dark:to-[#2a0f0f]"
      before={
        <>
          <div className="absolute inset-0 opacity-28 pointer-events-none dark:hidden" key={`light-${theme}`}>
            <MemoIridescence color={IRIDESCENCE_LIGHT} speed={0.55} amplitude={0.12} />
          </div>
          <div className="absolute inset-0 opacity-18 pointer-events-none hidden dark:block" key={`dark-${theme}`}>
            <MemoIridescence color={IRIDESCENCE_DARK} speed={0.55} amplitude={0.1} />
          </div>
        </>
      }
    >
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <Card className="plan-card shadow-2xl rounded-md p-6 sm:p-8 backdrop-blur flex flex-col gap-6">
            <div className="flex flex-col items-center text-center gap-4">
              <Badge>Test de velocidad</Badge>
              <div className="flex gap-2 flex-col">
                <h1 className="text-3xl md:text-5xl tracking-tighter max-w-3xl text-center font-regular">
                  Medí tu internet en segundos
                </h1>
                <p className="text-lg leading-relaxed tracking-tight text-muted-foreground max-w-3xl text-center">
                  Usamos LibreSpeed para medir descarga, subida y latencia. Simple y rápido.
                </p>
              </div>
              <Button
                asChild
                variant="outline"
                className="rounded-md px-5 py-2 bg-white text-black border-black/10 hover:bg-white hover:text-black dark:bg-white dark:text-black dark:border-black/10"
              >
                <Link to="/diagnostico">Ir al diagnóstico</Link>
              </Button>
            </div>

            <div className="rounded-md border border-border bg-card overflow-hidden shadow-inner">
              <div className="relative w-full h-[380px] sm:h-[400px] lg:h-[420px] bg-black/5 dark:bg-white/5 overflow-hidden">
                {!isIframeLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/5 dark:bg-white/5">
                    <span className="text-sm text-muted-foreground">Cargando test...</span>
                  </div>
                )}
                <iframe
                  title="LibreSpeed"
                  src="https://librespeed.org"
                  className={`block origin-top-left scale-50 w-[200%] h-[200%] transition-opacity duration-300 ${isIframeLoaded ? "opacity-100" : "opacity-0"}`}
                  onLoad={() => setIsIframeLoaded(true)}
                  scrolling="no"
                ></iframe>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-muted-foreground">
              Para una medición más precisa podés usar{" "}
              <a
                href="https://www.speedtest.net/"
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-4 hover:text-foreground"
              >
                Speedtest
              </a>
              .
            </p>
          </Card>
        </div>
      </main>
    </PageLayout>
  );
};

export default SpeedTestPage;



